import { useState, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { ZInput, ZTextarea } from "@/components/ZInput";
import ZButton from "@/components/ZButton";
import Logo from "@/components/Logo";

const CITIES = ["Conakry", "Boké", "Faranah", "Kankan", "Kindia", "Labé", "Mamou", "N'Zérékoré", "Siguiri", "Autre"];
const ORG_TYPES = [
  { id: "particulier", label: "Particulier" },
  { id: "association", label: "Association" },
  { id: "entreprise", label: "Entreprise" },
  { id: "institution", label: "Institution" },
];

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const role = params.get("role") === "organizer" ? "organizer" : "buyer";

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "",
    org_type: "", org_name: "", city: "", bio: "", accept_terms: false,
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isBuyer = role === "buyer";
  const title = useMemo(() => isBuyer ? "Créer un compte acheteur" : "Créer un compte organisateur", [isBuyer]);

  const submitStep1 = (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return setErr("Votre nom est obligatoire.");
    if (!form.email.trim()) return setErr("E-mail obligatoire.");
    if (form.password.length < 6) return setErr("Mot de passe : 6 caractères minimum.");
    if (role === "organizer" && (!form.phone || form.phone.trim().length < 6)) {
      return setErr("Le téléphone est obligatoire pour un organisateur.");
    }
    if (isBuyer) {
      doRegister();
    } else {
      setStep(2);
    }
  };

  const submitStep2 = (e) => {
    e.preventDefault();
    setErr("");
    if (!form.org_type) return setErr("Sélectionnez votre type d'organisateur.");
    if (!form.org_name.trim()) return setErr("Le nom de la structure est obligatoire.");
    if (!form.city) return setErr("Sélectionnez votre ville.");
    if (!form.accept_terms) return setErr("Vous devez accepter les conditions organisateur.");
    doRegister();
  };

  const doRegister = async () => {
    setLoading(true);
    try {
      const payload = {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        role,
        phone: form.phone.trim() || null,
      };
      if (role === "organizer") {
        Object.assign(payload, {
          org_type: form.org_type,
          org_name: form.org_name.trim(),
          city: form.city,
          bio: form.bio.trim() || null,
          accept_terms: form.accept_terms,
        });
      }
      const user = await register(payload);
      if (user.role === "organizer" && user.needs_onboarding) nav("/onboarding-organizer");
      else if (user.role === "organizer") nav("/org");
      else nav("/events");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="z-container" style={{ maxWidth: 540 }} data-testid="register-page">
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <Logo to={null} size={28} />
      </div>
      <h1 className="z-page-title">{title}</h1>
      <p className="z-page-sub">
        {step === 2 ? "Étape 2/2 : votre structure" : isBuyer ? "Rejoignez la communauté en 30 secondes." : "Étape 1/2 : vos identifiants"}
      </p>

      {step === 1 && (
        <form onSubmit={submitStep1} className="z-card" style={{ padding: 24 }}>
          <ZInput
            label="Nom complet" value={form.name} onChange={(e) => set("name", e.target.value)}
            placeholder="Aïssa Bah" required data-testid="register-name"
          />
          <ZInput
            type="email" label="E-mail" value={form.email} onChange={(e) => set("email", e.target.value)}
            placeholder="vous@exemple.com" required data-testid="register-email"
          />
          <ZInput
            type="tel" label={`Téléphone${role === "organizer" ? "" : " (optionnel)"}`} value={form.phone}
            onChange={(e) => set("phone", e.target.value)} placeholder="+224 ..."
            required={role === "organizer"} data-testid="register-phone"
          />
          <ZInput
            type="password" label="Mot de passe" value={form.password}
            onChange={(e) => set("password", e.target.value)} placeholder="6 caractères minimum"
            required minLength={6} data-testid="register-password"
          />
          {err && <p className="z-error-text" data-testid="register-error">{err}</p>}
          <ZButton type="submit" block loading={loading} data-testid="register-next-btn">
            {isBuyer ? "Créer mon compte" : "Continuer"}
          </ZButton>
          {isBuyer && (
            <p style={{ textAlign: "center", marginTop: 10, fontSize: 12, color: "var(--z-muted)", lineHeight: 1.5 }}>
              En créant un compte, vous acceptez nos{" "}
              <Link to="/cgv" target="_blank" className="z-link">Conditions Générales</Link> et notre{" "}
              <Link to="/confidentialite" target="_blank" className="z-link">politique de confidentialité</Link>.
            </p>
          )}
          <div style={{ textAlign: "center", marginTop: 18, fontSize: 14, color: "var(--z-muted)" }}>
            Déjà inscrit ? <Link to="/login" className="z-link" data-testid="register-to-login">Se connecter</Link>
          </div>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={submitStep2} className="z-card" style={{ padding: 24 }}>
          <label className="z-label">Type d'organisateur</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8, marginBottom: 14 }}>
            {ORG_TYPES.map((t) => (
              <button
                key={t.id} type="button"
                onClick={() => set("org_type", t.id)}
                data-testid={`org-type-${t.id}`}
                className="z-chip"
                style={{
                  justifyContent: "center", padding: "12px 8px",
                  ...(form.org_type === t.id ? { background: "var(--z-teal)", color: "white", borderColor: "var(--z-teal)" } : {}),
                }}
              >{t.label}</button>
            ))}
          </div>

          <ZInput
            label="Nom de la structure" value={form.org_name}
            onChange={(e) => set("org_name", e.target.value)} placeholder="Mon Association"
            required data-testid="register-org-name"
          />

          <label className="z-label">Ville</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            {CITIES.map((c) => (
              <button
                key={c} type="button" onClick={() => set("city", c)}
                data-testid={`city-${c}`}
                className={`z-chip ${form.city === c ? "z-chip-active" : ""}`}
              >{c}</button>
            ))}
          </div>

          <ZTextarea
            label={`Bio (optionnel) — ${form.bio.length}/240`} value={form.bio}
            onChange={(e) => set("bio", e.target.value.slice(0, 240))}
            placeholder="Présentez votre structure en quelques mots."
            data-testid="register-bio"
          />

          <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: "var(--z-text)", marginBottom: 14, cursor: "pointer" }}>
            <input
              type="checkbox" checked={form.accept_terms}
              onChange={(e) => set("accept_terms", e.target.checked)}
              data-testid="register-accept-terms"
              style={{ marginTop: 3, width: 18, height: 18, accentColor: "var(--z-teal)" }}
            />
            <span>J'accepte les <Link to="/cgv" target="_blank" className="z-link" data-testid="register-cgv-link">Conditions Générales</Link> et la <Link to="/confidentialite" target="_blank" className="z-link" data-testid="register-privacy-link">politique de confidentialité</Link>.</span>
          </label>

          {err && <p className="z-error-text" data-testid="register-error">{err}</p>}

          <div style={{ display: "flex", gap: 10 }}>
            <ZButton type="button" variant="outline" onClick={() => setStep(1)} data-testid="register-back-btn">
              Retour
            </ZButton>
            <ZButton type="submit" block loading={loading} data-testid="register-submit-btn">
              Créer mon compte organisateur
            </ZButton>
          </div>
        </form>
      )}
    </div>
  );
}
